import {
  $mobx,
  observable,
  isObservable,
  isBoxedObservable,
  isComputed,
  isObservableArray,
  isObservableSet,
  isObservableMap,
  ownKeys,
} from 'mobx';
import type { Side } from '@/schemas/tiles';

// Internal factories accept ONLY fresh, unaliased deepClone(DTO) trees or locally
// created records/arrays. Never call them to certify an exposed replacement.
const sides = ['top', 'right', 'bottom', 'left'] as const;
const fields = [
  'tileType',
  'meta',
  'laneInputs',
  'laneOutputs',
  'bundleErrors',
  'bundle',
  'sides',
  'tint',
  'previewBlocked',
  'connections',
  ...sides,
];
const probe = observable.object(
  Object.fromEntries(fields.map((key) => [key, undefined])),
);
const probeArray = observable.array([]);
const objectAdmin = Object.getOwnPropertyDescriptor(probe, $mobx)?.value;
const arrayAdmin = Object.getOwnPropertyDescriptor(probeArray, $mobx)?.value;
const nativeObject =
  objectAdmin &&
  objectAdmin.values_ instanceof Map &&
  typeof objectAdmin.keysAtom_?.reportObserved === 'function';
const arrayDehancer =
  arrayAdmin && Object.getOwnPropertyDescriptor(arrayAdmin, 'dehancer');
const nativeArray =
  arrayAdmin &&
  Array.isArray(arrayAdmin.values_) &&
  typeof arrayAdmin.atom_?.reportObserved === 'function' &&
  arrayDehancer &&
  'value' in arrayDehancer &&
  arrayDehancer.value === undefined;
const objectAdminProto = nativeObject ? Object.getPrototypeOf(objectAdmin) : undefined;
const arrayAdminProto = nativeArray ? Object.getPrototypeOf(arrayAdmin) : undefined;
const probeValue = nativeObject ? objectAdmin.values_.get('meta') : undefined;
const valueDehancer =
  probeValue && Object.getOwnPropertyDescriptor(probeValue, 'dehancer');
const valueProto =
  probeValue &&
  typeof probeValue.reportObserved === 'function' &&
  valueDehancer &&
  'value' in valueDehancer &&
  valueDehancer.value === undefined
    ? Object.getPrototypeOf(probeValue)
    : undefined;
const atomProto = valueProto && Object.getPrototypeOf(valueProto);
const nativeReportObserved = atomProto?.reportObserved;
const nativeValueGet = valueProto?.get;
const nativeMapGet = Map.prototype.get;
const kindMarkers = [
  'isMobXObservableValue',
  'isMobXComputedValue',
  'isMobXObservableMap',
  'isMobXObservableSet',
];
const supported = !!(objectAdminProto && arrayAdminProto && valueProto);
const descriptors = new Map(
  fields.map((key) => [key, Object.getOwnPropertyDescriptor(probe, key)!]),
);
const nativeForEach = Object.getOwnPropertyDescriptor(Array.prototype, 'forEach')!;
const nativeFilter = Object.getOwnPropertyDescriptor(Array.prototype, 'filter');
const nativeArrayConstructor = Object.getOwnPropertyDescriptor(
  Array.prototype,
  'constructor',
);
const nativeArraySpecies = Object.getOwnPropertyDescriptor(Array, Symbol.species);
function sameDescriptor(a?: PropertyDescriptor, b?: PropertyDescriptor) {
  return (
    a?.value === b?.value &&
    a?.get === b?.get &&
    a?.set === b?.set &&
    a?.writable === b?.writable &&
    a?.enumerable === b?.enumerable &&
    a?.configurable === b?.configurable
  );
}
function ordinaryForEach(raw: unknown[]) {
  const current = Object.getOwnPropertyDescriptor(Array.prototype, 'forEach');
  return (
    !Object.getOwnPropertyDescriptor(raw, 'forEach') &&
    current &&
    current.value === nativeForEach.value &&
    current.get === nativeForEach.get &&
    current.set === nativeForEach.set &&
    current.writable === nativeForEach.writable &&
    current.enumerable === nativeForEach.enumerable &&
    current.configurable === nativeForEach.configurable
  );
}
const noLifecycleHooks = (atom: any) =>
  atom.onBOL === undefined && atom.onBUOL === undefined;
const bad = Symbol('unsupported visual data');
type Owned = { admin: any; proto: object | null; array: boolean };
const owned = new WeakMap<object, Owned>();

function remember(value: object, array: boolean) {
  if (!supported) return;
  const admin = Object.getOwnPropertyDescriptor(value, $mobx)?.value;
  if (
    !admin ||
    Object.getPrototypeOf(admin) !== (array ? arrayAdminProto : objectAdminProto)
  )
    return;
  if ((admin.proxy_ ?? admin.target_) !== value) return;
  if (
    array
      ? !Array.isArray(admin.values_) || typeof admin.atom_?.reportObserved !== 'function'
      : !(admin.values_ instanceof Map) ||
        typeof admin.keysAtom_?.reportObserved !== 'function'
  )
    return;
  owned.set(value, { admin, array, proto: Object.getPrototypeOf(value) });
}

/** Same default deep MobX enhancement; children are registered BEFORE exposure. */
export function ownVisualData<T>(privateClone: T): T {
  if (privateClone === null || typeof privateClone !== 'object') return privateClone;
  const array = Array.isArray(privateClone);
  const source = Object.getOwnPropertyDescriptors(privateClone);
  const copy: any = array ? new Array((privateClone as unknown[]).length) : {};
  for (const key of Object.keys(source)) {
    if (array && key === 'length') continue;
    const descriptor = source[key];
    if (!('value' in descriptor))
      throw new Error('Visual factory requires an owned data clone');
    Object.defineProperty(copy, key, {
      value: ownVisualData(descriptor.value),
      writable: true,
      enumerable: descriptor.enumerable,
      configurable: true,
    });
  }
  const result = observable(copy) as T;
  remember(result as object, array);
  return result;
}

/** Only absent type markers on an internally constructed native object may be
 * skipped. Custom prototypes, markers and missing-key read hooks use the normal
 * public MobX classification, without touching opaque proxies in this guard. */
function hasOrdinaryOwnedKind(value: object): boolean {
  const entry = record(value, false);
  if (
    !entry ||
    Map.prototype.get !== nativeMapGet ||
    entry.proto !== Object.prototype ||
    Object.getOwnPropertyDescriptor(value, $mobx)?.value !== entry.admin ||
    Object.getPrototypeOf(entry.admin) !== objectAdminProto
  )
    return false;
  const arrayMarker = 'isMobXObservableArrayAdministration';
  if (
    Object.getOwnPropertyDescriptor(entry.admin, arrayMarker) ||
    Object.getOwnPropertyDescriptor(objectAdminProto, arrayMarker) ||
    Object.getOwnPropertyDescriptor(Object.prototype, arrayMarker)
  )
    return false;
  const pending = Object.getOwnPropertyDescriptor(entry.admin, 'pendingKeys_');
  if (
    pending &&
    (!('value' in pending) ||
      (pending.value !== undefined &&
        (!(pending.value instanceof Map) ||
          Object.getPrototypeOf(pending.value) !== Map.prototype ||
          Object.getOwnPropertyDescriptor(pending.value, 'get') ||
          Map.prototype.get !== nativeMapGet)))
  )
    return false;
  for (const key of kindMarkers) {
    if (
      Object.getOwnPropertyDescriptor(value, key) ||
      Object.getOwnPropertyDescriptor(Object.prototype, key)
    )
      return false;
    const atom = pending?.value && nativeMapGet.call(pending.value, key);
    if (atom) {
      for (const field of [
        'value_',
        'dehancer',
        'onBOL',
        'onBUOL',
        'get',
        'reportObserved',
      ]) {
        const descriptor = Object.getOwnPropertyDescriptor(atom, field);
        if (descriptor && !('value' in descriptor)) return false;
      }
      if (
        Object.getPrototypeOf(atom) !== valueProto ||
        atom.dehancer !== undefined ||
        !noLifecycleHooks(atom) ||
        atom.get !== nativeValueGet ||
        atom.reportObserved !== nativeReportObserved
      )
        return false;
    }
  }
  return true;
}

/** Match public toJS traversal with one memo across owned and custom subtrees.
 * Ordinary property reads preserve interception/lifecycle ordering; the only
 * omitted dependencies are absent internal type-brand probes on proven data.
 * Normal additions (including nonenumerable brands) still notify ownKeys. Exact
 * private MobX dependency trees are not a contract; keep the differential tests
 * aligned with public toJS when upgrading MobX. */
export function snapshotOwnedVisualData<T>(source: T): T {
  const seen = new Map<object, any>();
  const copy = (value: any): any => {
    if (
      value == null ||
      typeof value !== 'object' ||
      value instanceof Date ||
      !isObservable(value)
    )
      return value;
    const ordinary = hasOrdinaryOwnedKind(value);
    if (!ordinary && (isBoxedObservable(value) || isComputed(value)))
      return copy(value.get());
    if (seen.has(value)) return seen.get(value);
    if (isObservableArray(value)) {
      const result = new Array(value.length);
      seen.set(value, result);
      value.forEach((child: unknown, index: number) => {
        result[index] = copy(child);
      });
      return result;
    }
    if (!ordinary && isObservableSet(value)) {
      const result = new Set();
      seen.set(value, result);
      value.forEach((child: unknown) => {
        result.add(copy(child));
      });
      return result;
    }
    if (!ordinary && isObservableMap(value)) {
      const result = new Map();
      seen.set(value, result);
      value.forEach((child: unknown, key: unknown) => {
        result.set(key, copy(child));
      });
      return result;
    }
    const result: Record<PropertyKey, unknown> = {};
    seen.set(value, result);
    ownKeys(value).forEach((key) => {
      if (Object.prototype.propertyIsEnumerable.call(value, key))
        result[key] = copy(value[key]);
    });
    return result;
  };
  return copy(source);
}

/** Called only by the native constructor after makeAutoObservable; no nested walk. */
export function registerVisualRoot(root: object): void {
  remember(root, false);
}

/** Publication snapshots retain their existing shallow-array mutation contract. */
export function ownVisualSignals<T>(signals: T[]): T[] {
  const result = observable.array(signals, { deep: false });
  remember(result, true);
  return result;
}

function record(value: unknown, array: boolean): Owned | undefined {
  if (value === null || typeof value !== 'object') return;
  const entry = owned.get(value); // Unknown/revoked/user Proxies stop HERE.
  if (!entry || entry.array !== array || Object.getPrototypeOf(value) !== entry.proto)
    return;
  if (
    Object.getOwnPropertyDescriptor(value, 'toJSON') ||
    (array && Object.getOwnPropertyDescriptor(value, 'forEach'))
  )
    return;
  if (
    Object.getOwnPropertyDescriptor(Object.prototype, 'toJSON') ||
    Object.getOwnPropertyDescriptor(Array.prototype, 'toJSON')
  )
    return;
  return entry;
}

function read(value: unknown, key: string): unknown | typeof bad {
  const entry = record(value, false);
  if (!entry) return bad;
  const descriptor = Object.getOwnPropertyDescriptor(value!, key);
  const node = entry.admin.values_.get(key);
  if (!descriptor && !node) {
    if (entry.proto && Object.getOwnPropertyDescriptor(entry.proto, key)) return bad;
    if (!noLifecycleHooks(entry.admin.keysAtom_)) return bad;
    entry.admin.keysAtom_.reportObserved();
    // An onBecomeObserved callback may add the key; retry through validation.
    if (entry.admin.values_.has(key) || Object.getOwnPropertyDescriptor(value!, key))
      return read(value, key);
    return entry.proto && Object.getOwnPropertyDescriptor(entry.proto, key)
      ? bad
      : undefined;
  }
  const expected = descriptors.get(key);
  if (
    !descriptor ||
    !expected ||
    descriptor.get !== expected.get ||
    descriptor.set !== expected.set ||
    !node ||
    Object.getPrototypeOf(node) !== valueProto ||
    node.dehancer !== undefined
  )
    return bad;
  if (!noLifecycleHooks(node)) return bad;
  node.reportObserved();
  // Supported observation callbacks can install interceptReads while subscribing.
  const current = Object.getOwnPropertyDescriptor(value!, key);
  if (
    entry.admin.values_.get(key) !== node ||
    node.dehancer !== undefined ||
    current?.get !== expected.get ||
    current?.set !== expected.set
  )
    return bad;
  const next = node.value_; // Exact native get result without invoking a read transform.
  if (next !== null && typeof next === 'object' && !owned.has(next)) return bad;
  return typeof next === 'function' ? bad : next;
}

function list(value: unknown, kind: 'signal' | 'index' | 'error'): any[] | typeof bad {
  const entry = record(value, true);
  if (!entry || entry.admin.dehancer !== undefined) return bad;
  const raw = entry.admin.values_ as unknown[];
  if (!ordinaryForEach(raw)) return bad;
  if (!noLifecycleHooks(entry.admin.atom_)) return bad;
  entry.admin.atom_.reportObserved();
  if (entry.admin.dehancer !== undefined || !ordinaryForEach(raw)) return bad;
  const result = new Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    const descriptor = Object.getOwnPropertyDescriptor(raw, String(i));
    if (!descriptor) continue; // Preserve holes: drawBundle.forEach skips them.
    if (!('value' in descriptor)) return bad;
    const v = descriptor.value;
    if (
      kind === 'signal'
        ? v !== 0 && v !== 1
        : kind === 'error'
          ? typeof v !== 'string'
          : !Number.isSafeInteger(v) || v < 0
    )
      return bad;
    result[i] = v;
  }
  return result;
}

/** Preserve pre-enhancement assignment payloads for object AND property interceptors. */
export function ownVisualAssignment<T>(root: object, key: string, privateValue: T): T {
  const clear = () => {
    const entry = record(root, false),
      expected = descriptors.get(key);
    if (!entry || !expected) return false;
    const node = entry.admin.values_.get(key),
      d = Object.getOwnPropertyDescriptor(root, key);
    return (
      d?.get === expected.get &&
      d?.set === expected.set &&
      node &&
      Object.getPrototypeOf(node) === valueProto &&
      node.dehancer === undefined &&
      (entry.admin.interceptors_ === undefined ||
        (Array.isArray(entry.admin.interceptors_) &&
          entry.admin.interceptors_.length === 0)) &&
      (node.interceptors_ === undefined ||
        (Array.isArray(node.interceptors_) && node.interceptors_.length === 0))
    );
  };
  if (!clear()) return privateValue;
  const prepared = ownVisualData(privateValue);
  return clear() ? prepared : privateValue; // A development spy may install a hook during creation.
}

/** Keep catalog filtering's public behavior, certifying only its native fresh result. */
export function filterVisualErrors(root: object, errors: string[], excluded: string) {
  const entry = record(errors, true);
  let fresh = false;
  if (
    entry &&
    !Object.getOwnPropertyDescriptor(entry.admin.values_, 'filter') &&
    !Object.getOwnPropertyDescriptor(entry.admin.values_, 'constructor') &&
    sameDescriptor(
      Object.getOwnPropertyDescriptor(Array.prototype, 'filter'),
      nativeFilter,
    ) &&
    sameDescriptor(
      Object.getOwnPropertyDescriptor(Array.prototype, 'constructor'),
      nativeArrayConstructor,
    ) &&
    sameDescriptor(
      Object.getOwnPropertyDescriptor(Array, Symbol.species),
      nativeArraySpecies,
    ) &&
    list(errors, 'error') !== bad
  ) {
    fresh = true;
    // Native graph error arrays are dense. Holes can expose inherited readers.
    for (let i = 0; i < entry.admin.values_.length; i++) {
      if (!Object.hasOwn(entry.admin.values_, i)) {
        fresh = false;
        break;
      }
    }
  }
  const filtered = errors.filter((error) => error !== excluded);
  return fresh ? ownVisualAssignment(root, 'bundleErrors', filtered) : filtered;
}

/** Prove bundle absence without invoking custom readers or lifecycle callbacks. */
export function isOwnedScalarTile(tile: unknown): boolean {
  const meta = read(tile, 'meta');
  if (meta === bad) return false;
  const bundle = read(meta, 'bundle');
  return bundle === undefined || bundle === null;
}

/** Paint-only snapshot; unknown identities are rejected before any object probe. */
export function readOwnedBundleVisual(tile: unknown) {
  if (read(tile, 'tileType') !== 'Wire' || read(tile, 'previewBlocked') !== false) return;
  const meta = read(tile, 'meta');
  if (meta === bad || read(meta, 'tint') !== undefined) return;
  // The native selector reads this override before drawing. It must not run
  // a custom reader that can change lanes after their paint snapshot.
  const connections = read(meta, 'connections');
  if (connections === bad) return;
  if (connections !== undefined) {
    if (!record(connections, false)) return;
    for (const side of sides) {
      if (typeof read(connections, side) !== 'boolean') return;
    }
  }
  const bundle = read(meta, 'bundle');
  if (!record(bundle, false)) return; // Label-only non-bundle wires remain fallback.
  const mapping = read(bundle, 'sides');
  if (mapping === bad || (mapping !== undefined && !record(mapping, false))) return;
  const inputs = read(tile, 'laneInputs'),
    outputs = read(tile, 'laneOutputs');
  if (inputs === bad || outputs === bad) return;
  const errors = list(read(tile, 'bundleErrors'), 'error');
  if (errors === bad) return;
  const values: Partial<Record<Side, number[]>> = {},
    maps: Partial<Record<Side, number[]>> = {};
  for (const side of sides) {
    const out = outputs === undefined ? undefined : read(outputs, side);
    const input = inputs === undefined ? undefined : read(inputs, side);
    if (out === bad || input === bad) return;
    const observed = out ?? input;
    if (observed !== undefined) {
      const signals = list(observed, 'signal');
      if (signals === bad) return;
      values[side] = signals;
    }
    const slots = mapping === undefined ? undefined : read(mapping, side);
    if (slots === bad) return;
    if (slots !== undefined) {
      const indices = list(slots, 'index');
      if (indices === bad) return;
      maps[side] = indices;
    }
  }
  return { meta: { bundle: { sides: maps } }, laneOutputs: values, bundleErrors: errors };
}
